import { apiClient } from '@/api/client';
import { AxiosHeaders, type AxiosRequestHeaders } from 'axios';
import type {
  ApiSuccessResponse,
  ConfirmSettingsContactChangeRequest,
  ConfirmSettingsContactChangeResponse,
  DeleteSettingsProfilePhotoResponse,
  RequestSettingsEmailChangeRequest,
  RequestSettingsPhoneChangeRequest,
  SettingsAccountDto,
  SettingsBusinessDto,
  SettingsContactChangeResponse,
  SettingsNotificationsDto,
  SettingsPreferencesDto,
  SettingsProfilePhotoUploadResponse,
  RevokeOtherSettingsSecuritySessionsResponse,
  RevokeSettingsSecuritySessionResponse,
  SettingsSecuritySessionsResponse,
  SettingsStoreDto,
  SettingsStoresDto,
  SettingsThemeDto,
  UpdateSettingsNotificationsRequest,
  UpdateSettingsPreferencesRequest,
  UpdateSettingsProfileRequest,
  UpdateSettingsBusinessRequest,
  UpdateSettingsStoreRequest,
  UpdateSettingsThemeRequest,
  SetPosPinRequest,
  VerifyPosPinRequest,
} from '@invento/shared';

type Envelope<T> = ApiSuccessResponse<T>;

export interface SettingsChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UploadSettingsProfilePhotoInput {
  file: File;
}

export interface UploadSettingsBrandingLogoInput {
  file: File;
}

const removeContentTypeForFormData = (data: unknown, headers?: AxiosRequestHeaders) => {
  if (headers instanceof AxiosHeaders) {
    headers.delete('Content-Type');
    return data;
  }

  if (headers) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }

  return data;
};

export const settingsService = {
  async getTheme(): Promise<Envelope<SettingsThemeDto>> {
    const response = await apiClient.get<Envelope<SettingsThemeDto>>('/settings/theme');
    return response.data;
  },

  async getBrandingLogo(): Promise<Blob> {
    const response = await apiClient.get<Blob>('/settings/branding/logo', {
      responseType: 'blob',
    });
    return response.data;
  },

  async uploadBrandingLogo(input: UploadSettingsBrandingLogoInput): Promise<Envelope<SettingsThemeDto>> {
    const formData = new FormData();
    formData.append('file', input.file);

    const response = await apiClient.post<Envelope<SettingsThemeDto>>(
      '/settings/branding/logo',
      formData,
      {
        transformRequest: [removeContentTypeForFormData],
      },
    );
    return response.data;
  },

  async deleteBrandingLogo(): Promise<Envelope<SettingsThemeDto>> {
    const response = await apiClient.delete<Envelope<SettingsThemeDto>>('/settings/branding/logo');
    return response.data;
  },

  async getAccount(): Promise<Envelope<SettingsAccountDto>> {
    const response = await apiClient.get<Envelope<SettingsAccountDto>>('/settings/account');
    return response.data;
  },

  async getBusiness(): Promise<Envelope<SettingsBusinessDto>> {
    const response = await apiClient.get<Envelope<SettingsBusinessDto>>('/settings/business');
    return response.data;
  },

  async updateBusiness(input: UpdateSettingsBusinessRequest): Promise<Envelope<SettingsBusinessDto>> {
    const response = await apiClient.patch<Envelope<SettingsBusinessDto>>('/settings/business', input);
    return response.data;
  },

  async updateProfile(input: UpdateSettingsProfileRequest): Promise<Envelope<SettingsAccountDto>> {
    const response = await apiClient.patch<Envelope<SettingsAccountDto>>('/settings/account/profile', input);
    return response.data;
  },

  async setPosPin(input: SetPosPinRequest): Promise<void> {
    await apiClient.put('/settings/account/pos-pin', input);
  },

  async verifyPosPin(input: VerifyPosPinRequest): Promise<void> {
    await apiClient.post('/settings/account/pos-pin/verify', input);
  },

  async requestEmailChange(
    input: RequestSettingsEmailChangeRequest,
  ): Promise<Envelope<SettingsContactChangeResponse>> {
    const response = await apiClient.post<Envelope<SettingsContactChangeResponse>>(
      '/settings/account/email-change/request',
      input,
    );
    return response.data;
  },

  async confirmEmailChange(
    input: ConfirmSettingsContactChangeRequest,
  ): Promise<Envelope<ConfirmSettingsContactChangeResponse>> {
    const response = await apiClient.post<Envelope<ConfirmSettingsContactChangeResponse>>(
      '/settings/account/email-change/confirm',
      input,
    );
    return response.data;
  },

  async requestPhoneChange(
    input: RequestSettingsPhoneChangeRequest,
  ): Promise<Envelope<SettingsContactChangeResponse>> {
    const response = await apiClient.post<Envelope<SettingsContactChangeResponse>>(
      '/settings/account/phone-change/request',
      input,
    );
    return response.data;
  },

  async confirmPhoneChange(
    input: ConfirmSettingsContactChangeRequest,
  ): Promise<Envelope<ConfirmSettingsContactChangeResponse>> {
    const response = await apiClient.post<Envelope<ConfirmSettingsContactChangeResponse>>(
      '/settings/account/phone-change/confirm',
      input,
    );
    return response.data;
  },

  async getProfilePhoto(): Promise<Blob> {
    const response = await apiClient.get<Blob>('/settings/account/profile-photo', {
      responseType: 'blob',
    });
    return response.data;
  },

  async uploadProfilePhoto(
    input: UploadSettingsProfilePhotoInput,
  ): Promise<Envelope<SettingsProfilePhotoUploadResponse>> {
    const formData = new FormData();
    formData.append('file', input.file);

    const response = await apiClient.post<Envelope<SettingsProfilePhotoUploadResponse>>(
      '/settings/account/profile-photo',
      formData,
      {
        transformRequest: [removeContentTypeForFormData],
      },
    );
    return response.data;
  },

  async deleteProfilePhoto(): Promise<Envelope<DeleteSettingsProfilePhotoResponse>> {
    const response = await apiClient.delete<Envelope<DeleteSettingsProfilePhotoResponse>>(
      '/settings/account/profile-photo',
    );
    return response.data;
  },

  async listSecuritySessions(): Promise<Envelope<SettingsSecuritySessionsResponse>> {
    const response = await apiClient.get<Envelope<SettingsSecuritySessionsResponse>>('/settings/security/sessions');
    return response.data;
  },

  async revokeSecuritySession(sessionId: string): Promise<Envelope<RevokeSettingsSecuritySessionResponse>> {
    const response = await apiClient.post<Envelope<RevokeSettingsSecuritySessionResponse>>(
      `/settings/security/sessions/${sessionId}/revoke`,
    );
    return response.data;
  },

  async revokeOtherSecuritySessions(): Promise<Envelope<RevokeOtherSettingsSecuritySessionsResponse>> {
    const response = await apiClient.post<Envelope<RevokeOtherSettingsSecuritySessionsResponse>>(
      '/settings/security/sessions/revoke-others',
    );
    return response.data;
  },

  async getNotifications(): Promise<Envelope<SettingsNotificationsDto>> {
    const response = await apiClient.get<Envelope<SettingsNotificationsDto>>('/settings/notifications');
    return response.data;
  },

  async getPreferences(): Promise<Envelope<SettingsPreferencesDto>> {
    const response = await apiClient.get<Envelope<SettingsPreferencesDto>>('/settings/preferences');
    return response.data;
  },

  async updatePreferences(input: UpdateSettingsPreferencesRequest): Promise<Envelope<SettingsPreferencesDto>> {
    const response = await apiClient.patch<Envelope<SettingsPreferencesDto>>('/settings/preferences', input);
    return response.data;
  },

  async updateNotifications(input: UpdateSettingsNotificationsRequest): Promise<Envelope<SettingsNotificationsDto>> {
    const response = await apiClient.patch<Envelope<SettingsNotificationsDto>>('/settings/notifications', input);
    return response.data;
  },

  async getStores(): Promise<Envelope<SettingsStoresDto>> {
    const response = await apiClient.get<Envelope<SettingsStoresDto>>('/settings/stores');
    return response.data;
  },

  async updateStore(storeId: string, input: UpdateSettingsStoreRequest): Promise<Envelope<SettingsStoreDto>> {
    const response = await apiClient.patch<Envelope<SettingsStoreDto>>(`/settings/stores/${storeId}`, input);
    return response.data;
  },

  async changePassword(input: SettingsChangePasswordRequest): Promise<void> {
    await apiClient.put('/auth/change-password', input);
  },

  async updateTheme(input: UpdateSettingsThemeRequest): Promise<Envelope<SettingsThemeDto>> {
    const response = await apiClient.patch<Envelope<SettingsThemeDto>>('/settings/theme', input);
    return response.data;
  },
};
